import React, { useContext, useMemo, useRef } from "react";
import {
  Platform,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";
import { useTheme } from "react-native-elements";
import { PlayerContext } from "../../backend/PlayerContext";
import { Item } from "../../backend/items/Item";
import { getItemById } from "../../backend/items/Items";
import { CraftingInfo } from "./CraftingDetails";
import { ItemInfo } from "./ItemInfo";
import { ItemTile } from "./ItemTile";

export interface ItemGridElement {
  itemId: string;
  amount: number;
  params: any;
  disabled?: boolean;
  disableTooltip?: boolean;
}

export interface ItemGridProps {
  items: ItemGridElement[];
  crafting?: boolean;
  onPressItem?: (item: Item, index: number) => void;
  onDoublePressItem?: (item: Item, index: number) => void;
  dummySlotAmount?: number;
  overrideMaxHeight?: number;
}

function ItemGridTooltipUnmemoized(props: {
  item: ItemGridElement | undefined;
  crafting: boolean | undefined;
}) {
  if (props.item == null) {
    return null;
  }
  const itemGridElement = props.item;
  const item = getItemById(itemGridElement.itemId);
  return useMemo(() => {
    return props.crafting ? (
      <CraftingInfo item={item} />
    ) : (
      <ItemInfo
        itemStack={{
          itemOccurrence: {
            itemId: item.getId(),
            params: itemGridElement.params,
          },
          amount: itemGridElement.amount,
        }}
        showTile={false}
      />
    );
  }, [JSON.stringify(itemGridElement), props.crafting]);
}

const ItemGridTooltip = React.memo(
  ItemGridTooltipUnmemoized,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps) == JSON.stringify(nextProps),
);

export function ItemGrid(props: ItemGridProps) {
  const theme = useTheme();
  const playerContext = useContext(PlayerContext);
  const { height, width } = useWindowDimensions();
  const estimatedMaxHeight = Math.max(220, height - 80 - 300 - 300);
  let playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const singlePressCallbacks = useMemo(() => {
    return props.items.map((itemGridElement, gridIndex) => () => {
      const item = getItemById(itemGridElement.itemId);
      props.onPressItem && props.onPressItem(item, gridIndex);
    });
  }, [
    props.items.map((itemGridElement) => itemGridElement.itemId).join(),
    props.onPressItem,
  ]);
  const doublePressCallbacks = useMemo(() => {
    return props.items.map((itemGridElement, gridIndex) => () => {
      const item = getItemById(itemGridElement.itemId);
      props.onDoublePressItem && props.onDoublePressItem(item, gridIndex);
    });
  }, [
    props.items.map((itemGridElement) => itemGridElement.itemId).join(),
    props.onDoublePressItem,
  ]);
  return useMemo(() => {
    let dummySlots = [];
    for (let i = 0; i < (props.dummySlotAmount || 0); i++) {
      dummySlots.push(<ItemTile title="Empty" key={`dummySlot${i}`} />);
    }
    return (
      <View
        style={[styles.container, { borderColor: theme.theme.colors.grey5 }]}
      >
        <ScrollView
          style={[
            props.overrideMaxHeight != null
              ? {
                maxHeight: props.overrideMaxHeight,
              }
              : { maxHeight: estimatedMaxHeight },
          ]}
          nestedScrollEnabled={true}
        >
          <View
            style={[
              styles.gridContainer,
              { backgroundColor: theme.theme.colors.background },
            ]}
          >
            {props.items.map((itemGridElement, index) => {
              const item = getItemById(itemGridElement.itemId);
              let accessibilityMarker = null;
              if (index % 10 == 0) {
                accessibilityMarker = (
                  <View
                    style={styles.accessibilityHidden}
                    accessible={true}
                    accessibilityLabel={`Item list position ${index + 1}`}
                    accessibilityRole="header"
                    // @ts-ignore This prop is supported
                    accessibilityLevel={5}
                    accessibilityElementsHidden={false}
                  />
                );
              }
              return (
                <View key={index}>
                  {accessibilityMarker}
                  <ItemTile
                    title={item.getName(itemGridElement.params)}
                    onPress={singlePressCallbacks[index]}
                    onDoublePress={doublePressCallbacks[index]}
                    icon={item.getId()}
                    disabled={itemGridElement.disabled}
                    amount={itemGridElement.amount}
                    tooltipIdSuffix={props.crafting ? "crafting" : "inventory"}
                    tooltip={
                      itemGridElement.disableTooltip ||
                        Platform.OS != "web" ? null : (
                        <ItemGridTooltip
                          item={itemGridElement}
                          crafting={props.crafting}
                        />
                      )
                    }
                  />
                </View>
              );
            })}
            {dummySlots}
          </View>
        </ScrollView>
      </View>
    );
  }, [
    JSON.stringify(props.items),
    props.onPressItem,
    props.onDoublePressItem,
    props.dummySlotAmount,
    props.crafting,
    estimatedMaxHeight,
    singlePressCallbacks,
    doublePressCallbacks,
    theme,
  ]);
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 2,
  },
  scrollView: {
    maxHeight: 220,
  },
  gridContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    flex: 1,
  },
  accessibilityHidden: {
    width: 1,
    height: 1,
    position: "absolute",
    left: 0,
    top: 0,
  },
});
